import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { connectComplete, createAppstoreOrder } from '@/api/cafe24Api';
import { PLANS } from '@/api/planApi';

type ViewState = 'loading' | 'success' | 'needs_login_prompt' | 'already_linked' | 'error';

/**
 * 카페24 OAuth 콜백 완료 후 도착하는 화면(§5.1 온보딩). NaverCallback/
 * KakaoCallback과 동일하게 최상위 독립 라우트 - ProtectedRoute로 감싸지
 * 않는다(needs_login+비로그인 상태에서도 렌더돼야 함).
 */
export const Cafe24Connect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [view, setView] = useState<ViewState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const hasRunRef = useRef(false);

  const mallId = searchParams.get('mallId');
  const status = searchParams.get('status');
  const [upgradingPlanId, setUpgradingPlanId] = useState<string | null>(null);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);

  const handleUpgrade = async (planId: string) => {
    setUpgradeError(null);
    setUpgradingPlanId(planId);
    try {
      const order = await createAppstoreOrder(planId);
      window.location.href = order.confirmationUrl;
    } catch {
      setUpgradeError('결제 준비 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      setUpgradingPlanId(null);
    }
  };

  useEffect(() => {
    if (isLoading) return;

    if (!mallId || !status) {
      setView('error');
      setErrorMsg('잘못된 접근입니다.');
      return;
    }

    if (status === 'success') {
      setView('success');
      return;
    }

    if (status === 'already_linked') {
      // 이미 다른 계정에 ACTIVE로 연동된 몰을 재설치 시도한 경우(B/E callback()이
      // 소유권을 조용히 덮어쓰지 않고 이 상태로 리다이렉트함).
      setView('already_linked');
      return;
    }

    if (status === 'needs_login') {
      if (!isAuthenticated) {
        sessionStorage.setItem('redirectAfterLogin', `/cafe24/connect?mallId=${mallId}&status=needs_login`);
        setView('needs_login_prompt');
        return;
      }
      if (hasRunRef.current) return;
      hasRunRef.current = true;
      (async () => {
        try {
          await connectComplete(mallId);
          setView('success');
        } catch (err: any) {
          setView('error');
          setErrorMsg(
            err?.response?.status === 409
              ? '이미 다른 계정에 연동된 카페24 몰입니다. 관리자에게 문의해주세요.'
              : '계정 연결 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
          );
        }
      })();
      return;
    }

    setView('error');
    setErrorMsg('알 수 없는 연동 상태입니다.');
  }, [isLoading, isAuthenticated, mallId, status]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center px-4 max-w-sm">
        {view === 'loading' && (
          <>
            <Spinner size="2xl" className="mx-auto mb-4 text-primary" />
            <p className="text-gray-600">카페24 연동 상태를 확인하는 중...</p>
          </>
        )}
        {view === 'success' && (
          <>
            <p className="text-lg font-semibold text-gray-900 mb-2">카페24 연동이 완료됐습니다.</p>
            <p className="text-gray-600 mb-6">이제 신규 상품 이미지에 ALT 텍스트가 자동으로 채워집니다.</p>
            <div className="flex gap-2 justify-center mb-8">
              <Button onClick={() => navigate('/dashboard')}>대시보드로 이동</Button>
              <Button variant="outline" onClick={() => navigate('/dashboard/cafe24-app-guide')}>사용 가이드 보기</Button>
            </div>
            <div className="border-t pt-6">
              <p className="text-sm text-gray-500 mb-3">더 많은 이미지를 처리하고 싶으신가요?</p>
              <div className="flex gap-2 justify-center">
                {PLANS.filter((p) => p.code === 'STARTER' || p.code === 'PROFESSIONAL').map((plan) => (
                  <Button
                    key={plan.code}
                    variant="outline"
                    disabled={upgradingPlanId !== null}
                    onClick={() => handleUpgrade(plan.code)}
                  >
                    {upgradingPlanId === plan.code
                      ? '결제 준비 중...'
                      : `${plan.name} 구독하기 (월 ${plan.price?.toLocaleString()}원)`}
                  </Button>
                ))}
              </div>
              {upgradeError && <p className="text-red-600 text-sm mt-2">{upgradeError}</p>}
            </div>
          </>
        )}
        {view === 'needs_login_prompt' && (
          <>
            <p className="text-lg font-semibold text-gray-900 mb-2">계정 연결이 필요합니다.</p>
            <p className="text-gray-600 mb-6">로그인 또는 회원가입 후 카페24 몰 연동을 마무리해주세요.</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => navigate('/login')}>로그인</Button>
              <Button variant="outline" onClick={() => navigate('/signup')}>회원가입</Button>
            </div>
          </>
        )}
        {view === 'already_linked' && (
          <>
            <p className="text-lg font-semibold text-gray-900 mb-2">이미 다른 계정에 연동된 카페24 몰입니다.</p>
            <p className="text-gray-600 mb-6">관리자에게 문의해주세요.</p>
            <Button onClick={() => navigate('/dashboard')}>대시보드로 이동</Button>
          </>
        )}
        {view === 'error' && <p className="text-red-600">{errorMsg}</p>}
      </div>
    </div>
  );
};
